/* eslint-disable no-param-reassign */

import React, { useCallback, useRef } from 'react';
import Axios, { CancelToken, CancelTokenSource } from 'axios';
import * as Routes from '@/modules/routes';
import ReactDOM from 'react-dom';
import { useDebounceCallback } from 'usehooks-ts';
import useSlideShowContext from '@/components/admin/SlideShow/stores/useSlideShowContext';
import DropdownMenu from '@/components/common/Dropdown/DropdownMenu';
import { useDropdownEvents } from '@/components/common/Dropdown';
import styles from '../../Content/SlideObjectText/AnchoredDropdown/AnchoredDropdown.module.scss';
import PaddingEditor from './PaddingEditor';
import { PopoverProps } from '../types';
import usePopover from '../../hooks/usePopover';

type Data = {
  padding: number;
};

const PaddingPopover = ({ anchorId, openerClass, slideObject, styleDivRef }: PopoverProps) => {
  const ref = useRef<HTMLDivElement>();
  const cancelSourceRef = useRef<CancelTokenSource | null>(null);
  const dropdownEvents = useDropdownEvents(ref);
  const updateSlideObjectInContext = useSlideShowContext(state => state.updateSlideObject);

  const { coordinates, setIsUpdated } = usePopover({ anchorId, dropdownEvents, openerClass, ref });

  const updateSlideObject = (data: Data, cancelToken: CancelToken) => (
    Axios.patch(
      Routes.plato_api_slide_object_path(slideObject.id),
      { padding: data.padding },
      { cancelToken },
    )
      .then((res) => {
        updateSlideObjectInContext(res.data.slideObject);
      })
  );

  const handleSubmit = async (data: Data, closePopover = true): Promise<any> => {
    setIsUpdated(true);

    if (closePopover) dropdownEvents.setOpen(false);

    // Cancel any in-flight request
    if (cancelSourceRef.current) {
      cancelSourceRef.current.cancel();
    }
    // New cancel token source for this request
    const source = Axios.CancelToken.source();
    cancelSourceRef.current = source;

    try {
      return await updateSlideObject(data, source.token);
    } catch (err) {
      if (Axios.isCancel(err)) {
        // Request canceled
        return;
      }
      throw err;
    }
  };

  const debouncedSubmit = useCallback(useDebounceCallback(handleSubmit, 300), []);

  const handleChange = async (data: Data) => {
    const paddingValue = data.padding ? `${data.padding}px` : '';
    const targetElement = slideObject.modelType === 'Section'
      ? styleDivRef.current.getElementsByTagName('iframe')[0].style : styleDivRef.current.style;

    targetElement.padding = paddingValue;

    await debouncedSubmit(data, false);
  };

  return ReactDOM.createPortal(
    <DropdownMenu ref={ref} className={styles.margin} coordinates={coordinates} open={dropdownEvents.open}>
      {dropdownEvents.open && (
        <PaddingEditor
          initialValue={slideObject.padding}
          onChange={handleChange}
          onSubmit={handleSubmit}
        />
      )}
    </DropdownMenu>,
    document.body,
  );
};

export default PaddingPopover;
